import React, { ReactNode, useEffect, useState } from 'react';
import {
  AutoScrollActivator,
  closestCenter,
  DndContext,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@wix/wix-style-react-incubator/dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@wix/wix-style-react-incubator/dnd-kit/sortable';
import { restrictToWindowEdges } from '@wix/wix-style-react-incubator/dnd-kit/modifiers';
import { observer } from 'mobx-react-lite';
import { FiltersMap } from '@wix/bex-core';
import { useWixPatternsContainer } from '@wix/bex-core/react';
import {
  DragAndDropCancel,
  DragAndDropSubmit,
  CollectionDragAndDropState,
  CollectionDragAndDropParentState,
} from '../DragAndDrop';
import { DragAndDropAnnouncements } from '../DragAndDrop/DragAndDropAnnouncements';
import { usePageContext } from '../../providers';

export interface ListDragAndDropContextProps<T, F extends FiltersMap> {
  state: CollectionDragAndDropParentState<T, F>;
  children?: ReactNode;
  onSubmit?: DragAndDropSubmit<T, F>;
  onCancel?: DragAndDropCancel<T, F>;
}

function _ListDragAndDropContext<T, F extends FiltersMap>(
  props: ListDragAndDropContextProps<T, F>,
) {
  const { state: parentState, children, onSubmit, onCancel } = props;

  const container = useWixPatternsContainer();
  const { modalsContainerRef } = usePageContext();

  const { keyedItems } = parentState.collection;

  const [state] = useState(
    () =>
      new CollectionDragAndDropState({
        state: parentState,
        container,
        a11yContainer: modalsContainerRef?.current,
      }),
  );

  const { dnd } = state;

  const pointerSensor = useSensor(PointerSensor, {
    activationConstraint: dnd.activationConstraint,
  });

  const keyboardSensor = useSensor(KeyboardSensor, {
    keyboardCodes: dnd.keyboardCodes,
    coordinateGetter: sortableKeyboardCoordinates,
  });

  // in jsdom we mock drag-and-drop by setting a custom events to draggable elements
  const sensors = useSensors(
    ...(process.env.NODE_ENV === 'test' ? [] : [pointerSensor, keyboardSensor]),
  );

  useEffect(
    () =>
      state.init({
        a11yContainer: modalsContainerRef?.current,
      }),
    [],
  );

  dnd.onSubmit = onSubmit;
  dnd.onCancel = onCancel;

  return (
    <DndContext
      sensors={sensors}
      autoScroll={{
        activator: AutoScrollActivator.DraggableRect,
        acceleration: 4,
      }}
      accessibility={{
        container: dnd.a11yContainer as HTMLElement | undefined,
        announcements: dnd.nullAnnouncements,
        screenReaderInstructions: {
          draggable: '',
        },
        restoreFocus: false,
      }}
      collisionDetection={closestCenter}
      modifiers={[restrictToWindowEdges]}
      cancelDrop={(ev) => dnd.beforeDrop(ev)}
      onDragMove={(ev) => {
        dnd.onDragMove(ev);
      }}
      onDragStart={(ev) => {
        dnd.onDragStart(ev);
      }}
      onDragEnd={(ev) => {
        dnd.onDragEnd(ev);
      }}
      onDragCancel={() => {
        dnd.onDragCancel();
      }}
      onDragOver={(ev) => {
        dnd.onDragOver(ev);
      }}
    >
      <SortableContext
        items={keyedItems}
        strategy={verticalListSortingStrategy}
      >
        {children}
        <DragAndDropAnnouncements state={dnd} dataHook="list-dnd-live-region" />
      </SortableContext>
    </DndContext>
  );
}

export const ListDragAndDropContext = observer(_ListDragAndDropContext);
